import { createContext, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

const WishlistContext = createContext(null);
export const useWishlist = () => useContext(WishlistContext);

const KEY = "tashora_wishlist";

export const WishlistProvider = ({ children }) => {
  const { user } = useAuth();
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch { return []; }
  });
  const syncedFor = useRef(null);

  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(items)); }, [items]);

  // When a user signs in, merge local wishlist with their account wishlist.
  useEffect(() => {
    if (!user) { syncedFor.current = null; return; }
    if (syncedFor.current === user.user_id) return;
    syncedFor.current = user.user_id;
    (async () => {
      try {
        const res = await api.get("/wishlist");
        const server = res.data || [];
        const serverIds = new Set(server.map((p) => p.id));
        const localOnly = items.filter((p) => !serverIds.has(p.id));
        await Promise.all(localOnly.map((p) => api.post("/wishlist", { product_id: p.id }).catch(() => {})));
        setItems([...server, ...localOnly]);
      } catch (e) { console.error("Failed to sync wishlist with account:", e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const has = (id) => items.some((p) => p.id === id);

  const toggle = (product) => {
    const exists = has(product.id);
    setItems((prev) => exists ? prev.filter((p) => p.id !== product.id) : [...prev, product]);
    toast[exists ? "message" : "success"](exists ? "Removed from wishlist" : "Saved to wishlist");
    if (user) {
      if (exists) api.delete(`/wishlist/${product.id}`).catch(() => {});
      else api.post("/wishlist", { product_id: product.id }).catch(() => {});
    }
  };

  const remove = (id) => {
    setItems((prev) => prev.filter((p) => p.id !== id));
    if (user) api.delete(`/wishlist/${id}`).catch(() => {});
  };

  return (
    <WishlistContext.Provider value={{ items, has, toggle, remove, count: items.length }}>
      {children}
    </WishlistContext.Provider>
  );
};
